<template>
    <ui-container class="py-8">
        <ui-header
            :title="editMode ? 'Editing Progress Report - '+this.item.formatted_date : 'Create new Progress Report'"
            :subtitle="editMode ? 'ID #'+this.item.id : null">
            <template #actions>
                <ui-button-new icon="fas fa-arrow-left" @click="edit(false)" v-if="editMode">Done</ui-button-new>
            </template>
        </ui-header>
        <div class="grid gap-6 grid-cols-12 py-6">
            <ui-date-picker label="Progress Report Date" name="date" v-model="form.date" class="xl:col-span-6"
                            placeholder="What is date for this progress report?"
                            :error="form.errors.get('date')"
                            :with-time="true"
                            :min-date="minDate()"
                            @input="save"></ui-date-picker>
            <ui-input label="Weather" name="weather" v-model="form.weather" placeholder="Dry, Rainy, etc"
                      class="xl:col-span-6" :error="form.errors.get('weather')" @input="save"></ui-input>
            <ui-input label="Work as Specification" name="work_as_spec" v-model="form.work_as_spec" placeholder="Yes"
                      class="xl:col-span-6" :error="form.errors.get('work_as_spec')" @input="save"></ui-input>
            <ui-input label="Unfixed materials on site" name="unfixed_materials_value"
                      v-model="form.unfixed_materials_value"
                      placeholder="xxx.xx"
                      class="xl:col-span-6" :error="form.errors.get('unfixed_materials_value')"
                      @input="save"></ui-input>
            <ui-input label="Quality of Workmanship" name="quality_of_work" v-model="form.quality_of_work"
                      placeholder="Excellent"
                      class="xl:col-span-6" :error="form.errors.get('quality_of_work')" @input="save"></ui-input>
            <ui-textarea autoresize label="House Keeping" name="housekeeping" v-model="form.housekeeping"
                         placeholder="House keeping for this progress report..."
                         class="xl:col-span-12" :error="form.errors.get('housekeeping')" @input="save"></ui-textarea>
            <ui-textarea autoresize label="Site Safety" name="site_safety" v-model="form.site_safety"
                         placeholder="Activities on site..."
                         class="xl:col-span-12" :error="form.errors.get('site_safety')" @input="save"></ui-textarea>
            <ui-textarea autoresize label="Comments" name="comments" v-model="form.comments" placeholder="Notes..."
                         class="xl:col-span-12" :error="form.errors.get('comments')" @input="save"></ui-textarea>
        </div>

        <div class="space-y-0" v-if="store.state.quotes">
            <hr class="py-1 my-0"/>
            <div class="w-auto py-2 my-2">
                <ui-toggle class="mb-0" :field="showCompleted?'text-green-700':'text-red-500'" label="Show Completed"
                           :text="totalHidden+'/'+totalItems+(!showCompleted?' Items are completed and hidden - toggle to unhide':' Items are completed - toggle to hide')"
                           name="show_completed" v-model="showCompleted"></ui-toggle>
            </div>
            <hr class="py-1 my-0"/>
            <template v-for="quote in controlledQuotes">
                <template
                    v-if="(showCompleted || quote.preliminaries.some((x) => (calculatePreviousForPrelim(x) !== '100%')) || quote.tasks.some((y) => y.items.some((x) => (calculatePreviousForItem(x) !== '100%') && (!x.is_omitted))))">
                    <label class="py-3 mt-4" :class="getItemClass(quote,item)" v-if="quote.is_variation">Variation
                        {{ quote.variation_number }} -
                        ({{ quote.reference }}) <span
                            class="text-xs text-gray-300"> - Secured at {{
                                quote.secured_at.toLocaleString()
                            }} </span></label>
                    <label class="py-3 mt-4" :class="getItemClass(quote,item)" v-else>Original Contract -
                        ({{ quote.reference }})
                        <span class="text-xs text-gray-300"> - Secured at {{
                                quote.secured_at.toLocaleString()
                            }} </span>
                    </label>
                    <div class="grid grid-cols-12 mt-2 border-gray-200 text-xs text-gray-500 font-semibold">
                        <div class="col-span-1 border-t border-l px-2 py-2" :class="getItemClass(quote)">Item</div>
                        <div class="col-span-8 border-t px-1 py-2" :class="getItemClass(quote)">Description</div>
                        <div class="col-span-1 border-t px-1 py-2" :class="getItemClass(quote)">Qty</div>
                        <div class="col-span-1 border-t px-1 py-2 text-center" :class="getItemClass(quote)">Previous
                        </div>
                        <div class="col-span-1 border-t border-r px-1 py-2 text-center" :class="getItemClass(quote)">
                            Submitted
                        </div>
                        <template v-for="task in quote.tasks">
                            <template
                                v-if="(showCompleted || task.items.some((x) => (calculatePreviousForItem(x) !== '100%') && (!x.is_omitted)))">
                                <div class="col-span-1 border-t border-l bg-gray-50 px-2 py-2"
                                     :class="getItemClass(quote,task)">{{ task.reference }}
                                </div>
                                <div class="col-span-11 border-t border-r bg-gray-50 px-1 py-2"
                                     :class="getItemClass(quote,task)">{{ task.description }}
                                </div>
                                <template v-for="item in task.items">
                                    <template v-if="shouldItemBeVisible(item)">
                                        <div class="col-span-1 border-t border-l px-4 py-2"
                                             :class="getItemClass(quote,item)">
                                            {{ item.reference }}
                                        </div>
                                        <div class="col-span-8 border-t px-1 py-2" :class="getItemClass(quote,item)">
                                            {{ item.description }}
                                        </div>
                                        <div class="col-span-1 border-t px-1 py-2" :class="getItemClass(quote,item)">{{
                                                item.qty
                                            }}
                                            {{ item.unit }}
                                        </div>
                                        <div class="col-span-1 border-t px-1 py-2 text-center"
                                             :class="calculateClass(calculatePreviousForItem(item))">
                                            {{ calculatePreviousForItem(item) }}
                                        </div>
                                        <div class="col-span-1 border-t border-r px-1 py-2 text-center">
                                            <input inputmode="numeric" v-model="form.new_item_values[item.id]"
                                                   @input="save"
                                                   style="margin-bottom: 0"
                                                   v-if="!item.is_omitted" :disabled="checkDate(quote.secured_at)">
                                        </div>
                                    </template>
                                </template>
                            </template>
                        </template>
                        <div class="col-span-12 border-t border-r px-0 py-0"></div>
                        <template
                            v-if="quote.preliminaries.length && (showCompleted || quote.preliminaries.some((x) => (calculatePreviousForPrelim(x) !== '100%')))">
                            <div class="col-span-12 py-2 px-1 mt-1 text-sm" :class="getItemClass(quote)">Preliminaries
                            </div>
                            <template v-for="preliminary in quote.preliminaries">
                                <template
                                    v-if="(showCompleted || calculatePreviousForPrelim(preliminary) !== '100%')">
                                    <div class="col-span-1 border-t border-l px-1 py-2"
                                         :class="getItemClass(quote,preliminary)">{{ preliminary.reference }}
                                    </div>
                                    <div class="col-span-8 border-t px-1 py-2" :class="getItemClass(quote,preliminary)">
                                        {{ preliminary.description }}
                                    </div>
                                    <div class="col-span-1 border-t px-1 py-2" :class="getItemClass(quote,preliminary)">
                                        {{ preliminary.qty }} {{ preliminary.unit }}
                                    </div>
                                    <div class="col-span-1 border-t px-1 py-2 text-center"
                                         :class="calculateClass(calculatePreviousForPrelim(preliminary))">
                                        {{ calculatePreviousForPrelim(preliminary) }}
                                    </div>
                                    <div class="col-span-1 border-t border-r px-1 py-2 text-center">
                                        <input inputmode="numeric" v-model="form.new_preliminary_values[preliminary.id]"
                                               @input="save"
                                               style="margin-bottom: 0" v-if="!preliminary.is_omitted"
                                               :disabled="checkDate(quote.secured_at)">
                                    </div>
                                </template>
                            </template>
                            <div class="col-span-12 border-t border-r px-0 py-0"></div>
                        </template>
                    </div>
                </template>
            </template>
            <div
                v-if="!showCompleted && (totalHidden === totalItems)"
                class="mt-6">
                <div class="rounded-xl my-4 bg-gray-50 border-gray-200 border p-4 text-green-600 italic text-center">
                    All Contracts and Variations have been completed
                </div>
            </div>

        </div>

        <ui-data-grid-item wide class="mt-6 flex" v-if="!editMode">
            <div class="flex flex-col md:flex-row justify-end flex-wrap">
                <ui-button-new v-if="editMode" @click="edit(false)" class="ml-3">Cancel</ui-button-new>
                <ui-button-new primary @click.prevent="form.submit()" :loading="form.busy" class="ml-3"
                               v-if="this.store.state.quotes.length">Create
                </ui-button-new>
            </div>
        </ui-data-grid-item>

    </ui-container>

</template>

<script>
import UiContainer from "../ui/UiContainer.vue";
import UiDatePicker from "../ui/UiDatePicker.vue";
import UiButtonNew from "../ui/UiButtonNew.vue";
import UiDataGridItem from "../ui/UiDataGridItem.vue";
import {progressReportStore} from "../../stores";
import UiHeader from "../ui/UiHeader.vue";
import UiInput from "../ui/UiInput.vue";
import UiToggle from "../ui/UiToggle.vue";

export default {
    components: {UiToggle, UiInput, UiHeader, UiDataGridItem, UiDatePicker, UiButtonNew, UiContainer},
    props: {
        item: Object,
        edit: Function,
        addItem: Function,
        selectItem: Function,
        store: progressReportStore,
        packageId: null,
    },
    data() {
        let initialData = this.item ? this.item.getFormAttributes(this.store) : {}
        initialData.new_item_values = {}
        initialData.new_preliminary_values = {}
        if (!this.item) {
            initialData.date = new Date().toISOString()
        }
        if (this.item) {
            this.store.state.quotes.forEach((quote) => {
                quote.tasks.forEach((task) => {
                    task.items.forEach((item) => {
                        initialData.new_item_values[item.id] = this.submissionForItem(item)
                    });
                });
                quote.preliminaries.forEach((prelim) => {
                    initialData.new_preliminary_values[prelim.id] = this.submissionForPrelim(prelim)
                });
            })
        }
        return {
            form: new Form(initialData, async (form) => {
                let data = form.export();
                data.package_id = this.packageId;
                let sorter = (items) => {
                    items.sort((a, b) => (a.date < b.date) ? 1 : ((b.date < a.date) ? -1 : 0));
                };
                if (this.editMode) {
                    this.store.update(this.item, data);
                    this.edit(true, sorter)
                } else {
                    let item = await this.store.create(data, this.packageId);

                    form.reset();
                    this.edit(false, sorter);
                    this.selectItem(item);
                }
            }),
            showCompleted: false,
            totalItems: 0,
            totalHidden: 0,
        }
    },
    computed: {
        editMode() {
            return !!this.item
        },
        controlledQuotes() {
            return this.store.state.quotes;
        }
    },
    methods: {
        save() {
            if (this.editMode) {
                this.form.submit()
            }
        },
        getItemClass(quote, item = {}) {
            let ret = (this.checkDate(quote.secured_at)) ? 'text-gray-300' : '';
            if (item.is_omitted) {
                ret = 'text-red-500 line-through';
            }
            return ret;
        },
        checkDate(date) {
            let formDate = new Date(this.form.date);
            return formDate < date;
        },
        calculatePreviousForItem(item) {
            const formDate = new Date(this.form.date);
            let previous_item = this.store.state.items.filter(el => el.date < formDate).find(el => el.submitted_item_values.find(el2 => el2.item_id === item.id));
            if (previous_item) {
                return previous_item.submitted_item_values.find(el => el.item_id === item.id).percent + '%'
            }
            return ''
        },
        calculatePreviousForPrelim(prelim) {
            const formDate = new Date(this.form.date);
            let previous_item = this.store.state.items.filter(el => el.date < formDate).find(el => el.submitted_preliminary_values.find(el2 => el2.preliminary_id === prelim.id));
            if (previous_item) {
                return previous_item.submitted_preliminary_values.find(el => el.preliminary_id === prelim.id).percent + '%'
            }
            return ''
        },
        submissionForItem(item) {
            if (this.item) {
                let submitted = this.item.submitted_item_values.find(el => el.item_id === item.id);
                if (submitted) {
                    return '' + submitted.percent;
                }
            }
            return ''
        },
        submissionForPrelim(prelim) {
            if (this.item) {
                let submitted = this.item.submitted_preliminary_values.find(el => el.preliminary_id === prelim.id);
                if (submitted) {
                    return '' + submitted.percent;
                }
            }
            return ''
        },
        calculateClass(value) {
            if (value === '') return '';
            if (value === '100%') return 'bg-green-100';
            return 'bg-yellow-100';
        },
        minDate() {
            const f = this.store.state.items.filter((i) => i.used_by_afp);
            if (f.length) {
                return f.reduce((a, c) => {
                    return (a.date > c.date ? a : c);
                }).date
            }
            return null;
        },
        shouldItemBeVisible(item) {
            return this.showCompleted || ((this.calculatePreviousForItem(item) !== '100%') && (!item.is_omitted))
        },
        recalc() {
            let hidden = 0
            let total = 0;
            this.controlledQuotes.forEach((q) => {
                q.preliminaries.forEach((p) => {
                    total++;
                    if (this.calculatePreviousForPrelim(p) === '100%') {
                        hidden++;
                    }
                })
                q.tasks.forEach((t) => {
                    t.items.forEach((i) => {
                        if (!i.is_omitted) {
                            total++;
                            if (this.calculatePreviousForItem(i) === '100%') {
                                hidden++;
                            }
                        }
                    })
                })
            })
            this.totalHidden = hidden
            this.totalItems = total
        }
    },
    mounted() {
        this.recalc()
    },
    beforeUpdate() {
        this.recalc()
    }
}
</script>
